import {
  Button,
  InlineAlert,
  SelectField,
  TextArea,
  TextField,
  Typography,
  AlertOutlineIcon,
} from '@eventbrite/marmalade';
import { useIntl } from 'react-intl';
import { CONTACT_REASONS, MAX_LENGTH_MESSAGE, messages } from './constants';
import type { ContactFormData, ContactFormErrors } from './types';
import { validateForm, hasErrors } from './validation';
import styles from './FormView.module.scss';

interface Props {
  formData: ContactFormData;
  formErrors: ContactFormErrors;
  showValidationBanner: boolean;
  onFieldChange: (field: keyof ContactFormData, value: string) => void;
  onBack: () => void;
  onContinue: () => void;
  onSetErrors: (errors: ContactFormErrors) => void;
  onSetShowValidationBanner: (show: boolean) => void;
}

export function FormView({
  formData,
  formErrors,
  showValidationBanner,
  onFieldChange,
  onBack,
  onContinue,
  onSetErrors,
  onSetShowValidationBanner,
}: Props) {
  const intl = useIntl();

  const reasonOptions = CONTACT_REASONS.map(({ value, messageKey }) => ({
    value,
    label: intl.formatMessage(messages[messageKey]),
  }));

  const handleContinue = () => {
    const errorMessages = {
      emptyField: intl.formatMessage(messages.emptyFieldError),
      invalidEmail: intl.formatMessage(messages.invalidEmailError),
      messageTooLong: intl.formatMessage(messages.messageTooLongError, {
        maxLength: MAX_LENGTH_MESSAGE,
      }),
    };
    const errors = validateForm(formData, errorMessages);

    if (hasErrors(errors)) {
      onSetErrors(errors);
      onSetShowValidationBanner(true);
    } else {
      onSetErrors({});
      onSetShowValidationBanner(false);
      onContinue();
    }
  };

  const handleFieldChange = (field: keyof ContactFormData) => (value: string) => {
    onFieldChange(field, value);
  };

  const privacyText = intl.formatMessage(messages.privacyNotice, {
    privacyLink: (chunks: React.ReactNode) => (
      <a
        href="/privacypolicy/"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.privacyLink}
      >
        {chunks}
      </a>
    ),
  });

  return (
    <div className={styles.root}>
      {showValidationBanner && (
        <InlineAlert
          variant="danger"
          description={intl.formatMessage(messages.formValidationBanner)}
          icon={
            <span>
              <AlertOutlineIcon />
            </span>
          }
        />
      )}

      <div className={styles.fieldRow}>
        <TextField
          id="contact-name"
          label={intl.formatMessage(messages.labelName)}
          value={formData.name}
          onChange={handleFieldChange('name')}
          hasError={!!formErrors.name}
          helperText={formErrors.name}
          required
        />
        <TextField
          id="contact-email"
          label={intl.formatMessage(messages.labelEmail)}
          type="email"
          value={formData.email}
          onChange={handleFieldChange('email')}
          hasError={!!formErrors.email}
          helperText={formErrors.email}
          required
        />
      </div>

      <SelectField
        id="contact-reason"
        label={intl.formatMessage(messages.labelReason)}
        value={formData.reason}
        onChange={handleFieldChange('reason')}
        options={reasonOptions}
        placeholder={intl.formatMessage(messages.selectPlaceholder)}
        hasError={!!formErrors.reason}
        helperText={formErrors.reason}
        required
      />

      <TextArea
        id="contact-message"
        label={intl.formatMessage(messages.labelMessage)}
        value={formData.message}
        onChange={handleFieldChange('message')}
        hasError={!!formErrors.message}
        helperText={formErrors.message}
        maxLength={MAX_LENGTH_MESSAGE}
        rows={4}
        required
      />

      <Typography variant="body-md" className={styles.privacyNotice}>
        {privacyText}
      </Typography>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onBack}>
          {intl.formatMessage(messages.backButton)}
        </Button>
        <Button variant="primary" onClick={handleContinue}>
          {intl.formatMessage(messages.continueButton)}
        </Button>
      </div>
    </div>
  );
}
